import { useState } from "react";
import axios from "axios";
import { useAuth, API } from "../../App";
import { useI18n } from "../../i18n/I18nProvider";
import { extractError } from "../../lib/errors";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Save, Play, Flag, Lock } from "lucide-react";
import { FRAMEWORKS as ALL_FRAMEWORKS } from "../../constants/frameworks";

/**
 * AuditPlanTab — ISO 19011 §6.2-6.3 Preparation phase.
 * Renders objectives, criteria, competence note, audit plan, opening meeting notes.
 * Save calls PUT /audits/{id}/plan; Start Execution calls POST /audits/{id}/start-execution.
 */
const AuditPlanTab = ({ audit, onRefresh }) => {
  const { token } = useAuth();
  const { t } = useI18n();
  const frameworks = ALL_FRAMEWORKS.map((f) => ({ value: f.id, label: f.short }));

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startingExec, setStartingExec] = useState(false);
  const [closing, setClosing] = useState(false);

  const [form, setForm] = useState({
    objectives: audit.objectives || "",
    auditor_competence_note: audit.auditor_competence_note || "",
    audit_plan: audit.audit_plan || "",
    opening_meeting_notes: audit.opening_meeting_notes || "",
    closure_statement: audit.closure_statement || "",
  });

  const savePlan = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/audits/${audit.audit_id}/plan`,
        {
          objectives: form.objectives,
          auditor_competence_note: form.auditor_competence_note,
          audit_plan: form.audit_plan,
          opening_meeting_notes: form.opening_meeting_notes,
          criteria: audit.criteria || [],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("audits.toast.plan_saved"));
      setEditing(false);
      onRefresh();
    } catch (e) {
      toast.error(extractError(e, t("audits.toast.plan_failed")));
    } finally {
      setSaving(false);
    }
  };

  const startExecution = async () => {
    setStartingExec(true);
    try {
      await axios.post(
        `${API}/audits/${audit.audit_id}/start-execution`,
        { opening_meeting_notes: form.opening_meeting_notes || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("audits.toast.execution_started"));
      onRefresh();
    } catch (e) {
      toast.error(extractError(e, t("audits.toast.execution_failed")));
    } finally {
      setStartingExec(false);
    }
  };

  const finalizeAudit = async () => {
    try {
      await axios.put(
        `${API}/audits/${audit.audit_id}/finalize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("audits.toast.finalized"));
      onRefresh();
    } catch (e) {
      toast.error(extractError(e, t("audits.toast.finalize_failed")));
    }
  };

  const closeAudit = async () => {
    if (!form.closure_statement.trim()) {
      toast.error(t("audits.plan.closure_statement_label") + " is required");
      return;
    }
    setClosing(true);
    try {
      await axios.post(
        `${API}/audits/${audit.audit_id}/close`,
        { closure_statement: form.closure_statement },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("audits.toast.closed"));
      onRefresh();
    } catch (e) {
      toast.error(extractError(e, t("audits.toast.close_failed")));
    } finally {
      setClosing(false);
    }
  };

  const phase = audit.phase || "initiation";
  const isClosed = phase === "closed";

  return (
    <div className="space-y-6">
      {/* Audit info summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-border">
          <CardContent className="pt-4 pb-4 space-y-2">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
              {t("audits.plan.scope_label")}
            </p>
            <p className="text-sm">{audit.scope_description || <span className="text-muted-foreground italic">—</span>}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4 space-y-2">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
              {t("audits.plan.frameworks_label")}
            </p>
            <div className="flex flex-wrap gap-1">
              {(audit.frameworks || []).map((fw) => (
                <Badge key={fw} variant="secondary" className="text-xs">
                  {frameworks.find((f) => f.value === fw)?.label || fw}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ISO 19011 editable fields */}
      <Card className="border-border">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm">{t("audits.tab.plan")}</CardTitle>
          {!isClosed && (
            <Button variant="ghost" size="sm" onClick={() => setEditing(!editing)}>
              {editing ? t("cancel") : t("edit")}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t("audits.plan.objectives_label")}
            </Label>
            {editing ? (
              <Textarea
                value={form.objectives}
                onChange={(e) => setForm({ ...form, objectives: e.target.value })}
                placeholder={t("audits.plan.objectives_ph")}
                rows={3}
              />
            ) : (
              <p className="text-sm whitespace-pre-wrap">
                {audit.objectives || <span className="text-muted-foreground italic">{t("audits.plan.no_objectives")}</span>}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t("audits.plan.competence_label")}
            </Label>
            {editing ? (
              <Textarea
                value={form.auditor_competence_note}
                onChange={(e) => setForm({ ...form, auditor_competence_note: e.target.value })}
                placeholder={t("audits.plan.competence_ph")}
                rows={2}
              />
            ) : (
              <p className="text-sm whitespace-pre-wrap">
                {audit.auditor_competence_note || <span className="text-muted-foreground italic">—</span>}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t("audits.plan.plan_label")}
            </Label>
            {editing ? (
              <Textarea
                value={form.audit_plan}
                onChange={(e) => setForm({ ...form, audit_plan: e.target.value })}
                placeholder={t("audits.plan.plan_ph")}
                rows={4}
              />
            ) : (
              <p className="text-sm whitespace-pre-wrap">
                {audit.audit_plan || <span className="text-muted-foreground italic">{t("audits.plan.no_plan")}</span>}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t("audits.plan.opening_notes")}
            </Label>
            {editing ? (
              <Textarea
                value={form.opening_meeting_notes}
                onChange={(e) => setForm({ ...form, opening_meeting_notes: e.target.value })}
                placeholder={t("audits.plan.opening_notes_ph")}
                rows={2}
              />
            ) : (
              <p className="text-sm whitespace-pre-wrap">
                {audit.opening_meeting_notes || <span className="text-muted-foreground italic">—</span>}
              </p>
            )}
          </div>

          {editing && (
            <Button onClick={savePlan} disabled={saving} className="w-full sm:w-auto">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "..." : t("audits.plan.save_btn")}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Closure statement (shown when in reporting or closed phase) */}
      {(phase === "reporting" || isClosed) && (
        <Card className="border-border">
          <CardContent className="pt-4 pb-4 space-y-3">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t("audits.plan.closure_statement_label")}
            </Label>
            {isClosed ? (
              <p className="text-sm whitespace-pre-wrap">{audit.closure_statement}</p>
            ) : (
              <>
                <Textarea
                  value={form.closure_statement}
                  onChange={(e) => setForm({ ...form, closure_statement: e.target.value })}
                  placeholder={t("audits.plan.closure_statement_ph")}
                  rows={3}
                />
                <Button onClick={closeAudit} disabled={closing} variant="destructive" size="sm">
                  <Lock className="w-4 h-4 mr-2" />
                  {closing ? "..." : t("audits.plan.close_btn")}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Phase action buttons */}
      {!isClosed && (
        <div className="flex flex-wrap gap-2 pt-2">
          {phase === "preparation" && (
            <Button onClick={startExecution} disabled={startingExec} variant="outline" size="sm">
              <Play className="w-4 h-4 mr-2" />
              {startingExec ? "..." : t("audits.plan.start_execution_btn")}
            </Button>
          )}
          {phase === "execution" && (
            <Button onClick={finalizeAudit} variant="outline" size="sm">
              <Flag className="w-4 h-4 mr-2" />
              {t("audits.plan.finalize_btn")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditPlanTab;
